import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { SessionService } from './session.service';
import {
  CreateTeamRequest,
  TeamInvitationLinkAccessResponse,
  TeamInvitationLinkResponse,
  TeamInvitationsResponse,
  PlayerTeamsResponse,
  TeamResponse
} from './teams-api.types';

@Injectable({ providedIn: 'root' })
export class TeamsApiService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly apiBaseUrl = environment.apiBaseUrl;

  createTeam(payload: CreateTeamRequest): Observable<TeamResponse> {
    return this.http.post<TeamResponse>(
      `${this.apiBaseUrl}/api/teams`,
      { team: payload },
      { headers: this.authHeaders() }
    );
  }

  getTeam(teamId: number): Observable<TeamResponse> {
    return this.http.get<TeamResponse>(`${this.apiBaseUrl}/api/teams/${teamId}`, {
      headers: this.authHeaders()
    });
  }

  getMyTeams(): Observable<PlayerTeamsResponse> {
    return this.http.get<PlayerTeamsResponse>(`${this.apiBaseUrl}/api/players/me/teams`, {
      headers: this.authHeaders()
    });
  }

  getMyInvitations(): Observable<TeamInvitationsResponse> {
    return this.http.get<TeamInvitationsResponse>(`${this.apiBaseUrl}/api/players/me/team-invitations`, {
      headers: this.authHeaders()
    });
  }

  acceptInvitation(invitationId: number): Observable<TeamResponse> {
    return this.http.post<TeamResponse>(
      `${this.apiBaseUrl}/api/team-invitations/${invitationId}/accept`,
      {},
      { headers: this.authHeaders() }
    );
  }

  createInvitationLink(teamId: number, invitedRole: 'player' | 'manager_equipe' = 'player'): Observable<TeamInvitationLinkResponse> {
    return this.http.post<TeamInvitationLinkResponse>(
      `${this.apiBaseUrl}/api/teams/${teamId}/invitations`,
      { invitation: { invited_role: invitedRole } },
      { headers: this.authHeaders() }
    );
  }

  getInvitationLinkAccess(token: string): Observable<TeamInvitationLinkAccessResponse> {
    return this.http.get<TeamInvitationLinkAccessResponse>(
      `${this.apiBaseUrl}/api/team-invitations/link/${encodeURIComponent(token)}`,
      { headers: this.authHeaders() }
    );
  }

  acceptInvitationByToken(token: string): Observable<TeamResponse> {
    return this.http.post<TeamResponse>(
      `${this.apiBaseUrl}/api/team-invitations/link/${encodeURIComponent(token)}/accept`,
      {},
      { headers: this.authHeaders() }
    );
  }

  removeTeamMember(teamId: number, userId: number): Observable<TeamResponse> {
    return this.http.delete<TeamResponse>(
      `${this.apiBaseUrl}/api/teams/${teamId}/members/${userId}`,
      { headers: this.authHeaders() }
    );
  }

  private authHeaders(): HttpHeaders {
    const token = this.session.token();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
